import {
  AfterViewInit,
  Compiler,
  Component,
  EventEmitter,
  Injector,
  Input,
  NgModuleFactory,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { ComponentStyle, StylingMode } from '../abcComponent.model';
import { AbcComponentService } from '../abcComponent.service';
import { ITextArea, TextAreaStyle, ValidationMessageMode, ValidationStatus } from './abcTextArea';

@Component({
    selector: 'abcTextArea',
    template: '<ng-template #abcTextArea></ng-template>',
    standalone: false
})
export class AbcTextAreaComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input('value') attrValue: any;
  @Input('matAutosizeMinRows') matAutosizeMinRows: number;
  @Input('placeHolderLabel') attrPlaceholderLabel: string; // this can be a label or placeholder
  @Input('class') attrClass: string;
  private _class: string;
  @Input()
  get class(): string {
    return this._class;
  }
  set class(val: string) {
    this._class = val;

    if (this.componentRef) {
      this.componentRef.class = val;
    }
  }

  private _width: string;
  @Input()
  get width(): string {
    return this._width;
  }
  set width(val: string) {
    this._width = val;

    if (this.componentRef) {
      this.componentRef.width = val;
    }
  }

  private _displayEditIcon: boolean;
  @Input()
  get displayEditIcon(): boolean {
    return this._displayEditIcon;
  }
  set displayEditIcon(val: boolean) {
    this._displayEditIcon = val;

    if (this.componentRef) {
      this.componentRef.displayEditIcon = val;
    }
  }

  private _matRequired: boolean;
  @Input()
  get matRequired(): boolean {
    return this._matRequired;
  }
  set matRequired(isRequired: boolean) {
    this._matRequired = isRequired;

    if (this.componentRef) {
      this.componentRef.matRequired = isRequired;
    }
  }

  private _height: string;
  @Input()
  get height(): string {
    return this._height;
  }
  set height(val: string) {
    this._height = val;

    if (this.componentRef) {
      this.componentRef.height = val;
    }
  }

  private _stylingMode: StylingMode;
  @Input()
  get stylingMode(): StylingMode {
    return this._stylingMode;
  }
  set stylingMode(val: StylingMode) {
    this._stylingMode = val;

    if (this.componentRef) {
      this.componentRef.stylingMode = val;
    }
  }

  private _validationMessageMode: ValidationMessageMode;
  @Input()
  get validationMessageMode(): ValidationMessageMode {
    return this._validationMessageMode;
  }
  set validationMessageMode(val: ValidationMessageMode) {
    this._validationMessageMode = val;

    if (this.componentRef) {
      this.componentRef.validationMessageMode = val;
    }
  }

  private _validationStatus;
  @Input()
  get validationStatus(): ValidationStatus {
    return this._validationStatus;
  }
  set validationStatus(val: ValidationStatus) {
    this._validationStatus = val;
    if (this.componentRef) {
      this.componentRef.validationStatus = val;
    }
  }

  private _value: any;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(val: any) {
    this._value = val;
    if (this.componentRef) {
      this.componentRef.value = val;
    }
  }

  private _name: string;
  @Input()
  get name(): string {
    return this._name;
  }
  set name(val: string) {
    this._name = val;
    if (this.componentRef) {
      this.componentRef.name = val;
    }
  }

  private _placeholder: string;
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(val: string) {
    this._placeholder = val;
    if (this.componentRef) {
      this.componentRef.placeholder = val;
    }
  }

  private _minHeight: number;
  @Input('minHeight')
  get minHeight(): number {
    return this._minHeight;
  }
  set minHeight(val: number) {
    this._minHeight = val;
    if (this.componentRef) {
      this.componentRef.minHeight = val;
    }
  }

  private _maxHeight: number;
  @Input()
  get maxHeight(): number {
    return this._maxHeight;
  }
  set maxHeight(val: number) {
    this._maxHeight = val;
    if (this.componentRef) {
      this.componentRef.maxHeight = val;
    }
  }

  private _readOnly: boolean;
  @Input()
  get readOnly(): boolean {
    return this._readOnly;
  }
  set readOnly(val: boolean) {
    this._readOnly = val;
    if (this.componentRef) {
      this.componentRef.readOnly = val;
    }
  }

  private _autoResizeEnabled: boolean;
  @Input()
  get autoResizeEnabled(): boolean {
    return this._autoResizeEnabled;
  }
  set autoResizeEnabled(val: boolean) {
    this._autoResizeEnabled = val;
    if (this.componentRef) {
      this.componentRef.autoResizeEnabled = val;
    }
  }

  private _focusStateEnabled: boolean;
  @Input()
  get focusStateEnabled(): boolean {
    return this._focusStateEnabled;
  }
  set focusStateEnabled(val: boolean) {
    this._focusStateEnabled = val;
    if (this.componentRef) {
      this.componentRef.focusStateEnabled = val;
    }
  }

  private _hoverStateEnabled: boolean;
  @Input()
  get hoverStateEnabled(): boolean {
    return this._hoverStateEnabled;
  }
  set hoverStateEnabled(val: boolean) {
    this._hoverStateEnabled = val;
    if (this.componentRef) {
      this.componentRef.hoverStateEnabled = val;
    }
  }

  private _activeStateEnabled: boolean;
  @Input()
  get activeStateEnabled(): boolean {
    return this._activeStateEnabled;
  }
  set activeStateEnabled(val: boolean) {
    this._activeStateEnabled = val;
    if (this.componentRef) {
      this.componentRef.activeStateEnabled = val;
    }
  }

  private _rtlEnabled: boolean;
  @Input()
  get rtlEnabled(): boolean {
    return this._rtlEnabled;
  }
  set rtlEnabled(val: boolean) {
    this._rtlEnabled = val;
    if (this.componentRef) {
      this.componentRef.rtlEnabled = val;
    }
  }

  private _spellcheck: boolean;
  @Input()
  get spellcheck(): boolean {
    return this._spellcheck;
  }
  set spellcheck(val: boolean) {
    this._spellcheck = val;
    if (this.componentRef) {
      this.componentRef.spellcheck = val;
    }
  }

  private _visible: boolean;
  @Input()
  get visible(): boolean {
    return this._visible;
  }
  set visible(val: boolean) {
    this._visible = val;
    if (this.componentRef) {
      this.componentRef.visible = val;
    }
  }

  private _disabled: boolean;
  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(val: boolean) {
    this._disabled = val;
    if (this.componentRef) {
      this.componentRef.disabled = val;
    }
  }
  private _id: string;
  @Input()
  get id(): string {
    return this._id;
  }
  set id(value) {
    this._id = value;
  }
  private _style: ComponentStyle;
  @Input()
  get style() {
    return this._style;
  }
  set style(data: ComponentStyle) {
    this._style = data;

    if (this.componentRef) {
      this.viewContainerRef.clear();
      this.createComponentInstance().subscribe((component) => {
        this.eventListener.forEach((element) => {
          component.addEventListener(element.type, element.listener);
        });
      });
    }
  }

  @Output() onChange = new EventEmitter();
  @Output() valueChange = new EventEmitter();
  @Output() onTextAreaInput = new EventEmitter();
  @ViewChild('abcTextArea', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  public htmlAttributes: ITextArea;
  private eventListener = [];
  private componentRef;

  constructor(
    private abcComponentService: AbcComponentService,
    private compiler: Compiler,
    private injector: Injector
  ) {
    this.style = ComponentStyle.DevExpress;
    this.rtlEnabled = this.abcComponentService.rtlEnabled;
  }

  ngOnInit() {
    this.setTextAreaAttributesIfAvailable();
    this.createComponentInstance().subscribe((component) => {
      this.eventListener.forEach((element) => {
        component.addEventListener(element.type, element.listener);
      });
    });
  }

  ngAfterViewInit() {
    this.abcComponentService.addInstance(this);
  }

  ngOnDestroy(): void {
    this.eventListener = [];
    this.abcComponentService.removeInstance(this);
  }

  createComponentInstance(): Observable<any> {
    const observable = new Observable((observer) => {
      // let componentRef: any;
      switch (this.style) {
        case ComponentStyle.Material: {
          import('../../../material/src/matTextArea/matTextArea.module').then((module) => {
            this.compiler.compileModuleAsync(module.MatTextAreaModule).then((moduleFactory: NgModuleFactory<any>) => {
              const moduleRef = moduleFactory.create(this.injector);
              const componentFactory = moduleRef.instance.resolveComponent();
              this.componentRef = this.abcComponentService.loadComponent(
                componentFactory.componentType,
                this.viewContainerRef
              ).componentRef.instance;
              this.componentRef.placeholder = this.placeholder;
              this.componentRef.value = this.value;
              this.componentRef.onChange.subscribe((event) => {
                this.dataChange(event);
              });
              this.setAttributes(this.componentRef);
              observer.next(this.componentRef);
            });
          });
          break;
        }

        case ComponentStyle.DevExpress: {
          import('../../../devExtreme/src/devExTextArea/devExTextArea.module').then((module) => {
            this.compiler.compileModuleAsync(module.DevExTextAreaModule).then((moduleFactory: NgModuleFactory<any>) => {
              const moduleRef = moduleFactory.create(this.injector);
              const componentFactory = moduleRef.instance.resolveComponent();
              this.componentRef = this.abcComponentService.loadComponent(
                componentFactory.componentType,
                this.viewContainerRef
              ).componentRef.instance;
              this.componentRef.placeholder = this.placeholder;
              this.componentRef.value = this.value;
              this.componentRef.displayEditIcon = this.displayEditIcon;
              this.componentRef.visible = this.visible;
              this.componentRef.matAutosizeMinRows = this.matAutosizeMinRows;
              this.setAttributes(this.componentRef);
              this.componentRef.onChange.subscribe((event) => {
                this.dataChange(event);
              });
              this.componentRef.onTextAreaInput.subscribe((event) => {
                this.textAreaInput(event);
              });
              this.componentRef.valueChange.subscribe((event) => {
                this._value = event;
                this.valueChange.emit(event);
              });
              observer.next(this.componentRef);
            });
          });
          break;
        }
      }
    });
    return observable;
  }

  setAttributes(componentRef) {
    if (this.htmlAttributes.id) componentRef.id = this.htmlAttributes.id;
    if (this.htmlAttributes.matAutosizeMinRows)
      componentRef.matAutosizeMinRows = this.htmlAttributes.matAutosizeMinRows;
    if (this.htmlAttributes.placeholder) componentRef.placeholder = this.htmlAttributes.placeholder;
    if (this.htmlAttributes.value) componentRef.value = this.htmlAttributes.value;
    if (this.htmlAttributes.class) componentRef.class = this.htmlAttributes.class;
    if (this.componentRef.setTextAreaStyle) componentRef.setTextAreaStyle(this.htmlAttributes.setTextAreaStyle());
    this.componentRef.stylingMode = this.htmlAttributes.setTextAreaStyle().stylingMode
      ? this.htmlAttributes.setTextAreaStyle().stylingMode
      : this.abcComponentService.stylingMode;
    this.componentRef.rtlEnabled = this.rtlEnabled ? this.rtlEnabled : false;
  }

  // If the component is created inside HTML
  setTextAreaAttributesIfAvailable() {
    if (this.id) {
      const style: TextAreaStyle = {
        height: this.height ? this.height : 25,
        width: this.width ? this.width : '100%',
        disabled: this.disabled,
        minHeight: this.minHeight,
        maxHeight: this.maxHeight,
        class: this.class,
        autoResizeEnabled: this.autoResizeEnabled,
        activeStateEnabled: this.activeStateEnabled,
        focusStateEnabled: this.focusStateEnabled,
        hoverStateEnabled: this.hoverStateEnabled,
        name: this.name,
        readOnly: this.readOnly,
        matRequired: this.matRequired,
        rtlEnabled: this.rtlEnabled,
        spellcheck: this.spellcheck,
        stylingMode: this.stylingMode,
        validationMessageMode: this.validationMessageMode,
        validationStatus: (this.validationStatus = this.validationStatus = ValidationStatus.Valid),
        visible: (this.visible = true),
      };
      this.htmlAttributes = {
        id: this.id,
        placeholder: this.attrPlaceholderLabel,
        // matAutosizeMinRows: this.matAutosizeMinRows,
        value: this.value,
        setTextAreaStyle: (_textAreaStyle: TextAreaStyle) => (_textAreaStyle = style),
      };
    }
  }

  private dataChange(newValue) {
    this.onChange.emit({ newValue: newValue });
  }
  public textAreaInput(event): void {
    this.onTextAreaInput.emit(event);
  }
}
